import {
  ConnectionQualityLevels,
  ConnectionStates,
  DeviceErrors,
  StreamStates,
} from "@minervaproject/avsubsystem-core";

import { NavigationTimingMetricsType } from "./monitoring";
import { PreclassTestOutcomeType, PreclassTestResultType } from "./preclass-check";
import { WebsocketStatusType } from "./pubsub";

export const PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE = "pubsub-virtual-class-state";
export const REDIS_PUBSUB_VIRTUAL_CLASS_STATE_CHANNEL = "redis-pubsub-virtual-class-state";

/** Ping RTT stats for one socket (pubsub or sharedb). */
export interface PingRttType {
  isHigh: boolean;
  isLow: boolean;
  median: number | undefined;
  min: number;
  max: number;
}

/** Combined socket state for display; stored as separate flat fields. */
export interface SocketUserState {
  pingRtt?: PingRttType;
  websocketStatus?: WebsocketStatusType;
  websocketStatusReason?: string;
}

export interface PubsubVirtualUserState {
  /** Set on join: which websocket (tab) is this user's main AV connection. */
  activeWebsocketId?: string | null;
  avRtt?: string;
  cameraError?: DeviceErrors;
  connectionQuality?: ConnectionQualityLevels;
  connectionState?: ConnectionStates;
  currentCamera?: string;
  currentMic?: string;
  currentSpeaker?: string;
  isAudioEnabled?: boolean;
  isPresent?: boolean;
  isSpeaking?: boolean;
  lastSpokeAt?: number | null;
  isVideoEnabled?: boolean;
  micError?: DeviceErrors;
  navigationTimingMetrics?: NavigationTimingMetricsType;
  preclassCheck?: {
    outcome: PreclassTestOutcomeType;
    tests: PreclassTestResultType[];
  };
  publishedStreamState?: StreamStates;
  userAgent?: string;

  // Socket state for pubsub.
  pubsubPingRtt?: PingRttType;
  pubsubWebsocketStatus?: WebsocketStatusType;
  pubsubWebsocketStatusReason?: string;

  // Socket state for sharedb.
  shareDBPingRtt?: PingRttType;
  shareDBWebsocketStatus?: WebsocketStatusType;
  shareDBWebsocketStatusReason?: string;
}

export type PubsubVirtualUsersState = Record<string, PubsubVirtualUserState>;

export interface PubsubVirtualClassState {
  users: PubsubVirtualUsersState;
  last_updated_at: number | null;
}

export enum AvTabState {
  NotMain = "not-main",
  Main = "main",
  Retrying = "retrying",
}

export interface ClientLocalPubsubVirtualClassState extends PubsubVirtualClassState {
  av_tab_state?: AvTabState;
  observing_session_id: string | null;
  max_participant_bar_slots: number;
  // The visible class user ids in the participant bar. These are the users that fit in max_participant_bar_slots.
  participant_bar_visible_class_user_ids: string[];
  participant_bar_featured_class_user_ids: {
    id: string;
    timestamp: number;
  }[];
}
type PubsubVirtualUserAlreadyJoinedMessageType = {
  action: "already-joined";
  virtual_user_id: string;
};
type PubsubVirtualUserMuteMessageType = {
  action: "mute";
};
type PubsubVirtualUserSyncMessageType = {
  action: "sync";
  data: {
    users: PubsubVirtualUsersState;
  };
};
type PubsubVirtualUserUpdateMessageFromServerType = {
  action: "update";
  data: PubsubVirtualUserState;
  virtual_user_id: string;
};

export type PubsubVirtualClassStateMessageFromServerType = {
  type: typeof PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE;
  last_updated_at: number;
} & (
  | PubsubVirtualUserSyncMessageType
  | PubsubVirtualUserAlreadyJoinedMessageType
  | PubsubVirtualUserMuteMessageType
  | PubsubVirtualUserUpdateMessageFromServerType
);

export type PubsubVirtualUserJoinMessageType = {
  action: "join";
  force: boolean;
};
export type PubsubVirtualUserLeaveMessageType = {
  action: "leave";
};

export type PubsubVirtualUserMuteAllMessageType = {
  action: "mute-all";
  user_ids: string[];
};

export type PubsubVirtualUserUpdateMessageFromClientType = {
  action: "update";
  data: PubsubVirtualUserState;
  persist?: boolean;
};

export type BarePubsubVirtualClassStateMessageFromClientType =
  | PubsubVirtualUserJoinMessageType
  | PubsubVirtualUserLeaveMessageType
  | PubsubVirtualUserMuteAllMessageType
  | PubsubVirtualUserUpdateMessageFromClientType;

export type PubsubVirtualClassStateMessageFromClientType = {
  type: typeof PUBSUB_VIRTUAL_CLASS_STATE_MESSAGE_TYPE;
  virtual_user_id: string;
  class_session_id?: string | null;
} & BarePubsubVirtualClassStateMessageFromClientType;

export type RedisPubsubVirtualUserUpdateMessageType = {
  type: typeof REDIS_PUBSUB_VIRTUAL_CLASS_STATE_CHANNEL;
  section_id: string;
  virtual_user_id: string;
  data: PubsubVirtualUserState;
  last_updated_at: number;
}; // We also have pubsub messages that are not related to models to distribute the state of the virtual classroom.
